import styles from './index.module.css'

import * as React from 'react'

import api from '@disco/api/server'
import {
  addToDisco,
  addToDiscoCancel,
  getSignedPublicPageUrl,
  setAddToDiscoPlaylistPassword,
} from '@disco/state-management/actions/PublicPlaylistActions'

import type { ClientLibraryId } from 'types/client-library'
import type { AddToDiscoObject, Business } from 'types/public-playlist'
import type { LoginDetails } from 'types/user'

import { Dialog, Flex, VisuallyHidden } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import AddToDiscoSuccessModal from '../add-to-disco-success-modal'
import AddToDiscoBusinessList from './business-list'
import AddToDiscoLoginForm from './login-form'
import PlaylistPasswordForm from './playlist-password-form'
import type { AddToDiscoUser, State } from './types'
import { useTranslation } from '@disco/i18n/use-translation'

export type User = AddToDiscoUser

type Props = {
  currentBusinessId: number
  playlistId: number
  playlistVersionId: number
  shareId: number | null | undefined
  campaignId?: number | null | undefined
  recipientId?: number | null | undefined
  object: AddToDiscoObject
  businesses?: Array<Business>
  success: boolean
  user: User
  onRequestClose: (...args: Array<any>) => any
  showModal: boolean
  href?: string
  clientLibraryId?: ClientLibraryId
  showModalSuccessDialog?: boolean
  displayPasswordRestoreLink?: boolean | string
  anonymousBusiness?: boolean
  mixpanelEventDetails?: Record<string, any>
  hideSuccessModal?: boolean
  colorTheme?: string
}

function AddToDiscoModal({
  currentBusinessId,
  playlistId,
  playlistVersionId,
  shareId,
  campaignId,
  recipientId,
  object,
  businesses = [],
  success,
  user,
  onRequestClose,
  showModal,
  href,
  clientLibraryId,
  showModalSuccessDialog,
  mixpanelEventDetails,
  hideSuccessModal,
}: Props): React.ReactNode {
  const { t } = useTranslation('shares')
  const dispatch = useDispatch()
  const [state, setState] = React.useState<State>({
    loading: false,
    status: 'INITIAL',
    requestedBusinessId: null,
    error: null,
    selectedBusiness: null,
    // Land on the sign-in step (not the gateway) if we're remounting after a
    // failed auth retry, so the error message is visible instead of silently
    // resetting to the initial "Save to DISCO" screen.
    showSignIn: Boolean(
      user.showLoginError || user.error || user.mfaCodeRequired,
    ),
  })

  const playlistPasswordPrompt = useSelector(
    rootState => rootState.discoAdd?.addToDiscoModal?.playlistPasswordPrompt,
  )

  const getUrl = () => href || getSignedPublicPageUrl()

  const handleForgotPassword = (email: string, returnUrl: string) =>
    api.sendPasswordRecoveryInstructions(email, returnUrl)

  const signupUrlAsync = async () => {
    const host = 'https://accounts.disco.ac'
    let trackfile_ids

    switch (object.type) {
      case 'PLAYLIST':
        trackfile_ids = null
        break
      case 'SELECTION':
      case 'SECTION':
        trackfile_ids = object.ids
        break
      case 'TRACK': {
        const all_files = object.track.all_files || {}
        trackfile_ids = Object.keys(all_files).map(f => ({
          track_file_id: all_files[f].id,
          format: f,
        }))
        break
      }
    }

    let soUrlPart = ''
    try {
      if (trackfile_ids && playlistVersionId) {
        const res = await api.getCustomDownloadUrl(
          window.document.location.href,
          playlistVersionId,
          trackfile_ids,
        )
        soUrlPart = `so=${res.id}&`
      }
    } catch (err) {
      console.error(err)
    }

    return `${host}/signup?utm_source=save_to_disco_modal&${soUrlPart}su=${window.encodeURIComponent(
      getUrl(),
    )}`
  }

  const handleAddToDisco = (
    business: { url: string; id: number; name: string } | null | undefined,
    loginDetails?: LoginDetails,
  ) => {
    dispatch(
      addToDisco(
        business
          ? { id: business.id, name: business.name, url: business.url }
          : null,
        getUrl(),
        object,
        loginDetails,
        {
          shareId,
          playlistId,
          campaignId,
          recipientId,
          clientLibraryId,
          showModalSuccessDialog,
        },
        mixpanelEventDetails,
      ),
    )
  }

  const handleLogin = (data: any) => {
    const business = data.mfa_code ? state.selectedBusiness : null
    handleAddToDisco(business, {
      username: data.username,
      password: data.password,
      captcha: data.captcha,
      mfa_code: data.mfa_code,
    })
  }

  const handlePlaylistPasswordSubmit = (password: string) => {
    if (!playlistPasswordPrompt) return
    dispatch(
      setAddToDiscoPlaylistPassword(
        playlistPasswordPrompt.playlistId,
        password,
        true,
      ),
    )
    handleAddToDisco(playlistPasswordPrompt.destBusiness)
  }

  const handleSaveBusiness = (business: Business) => {
    setState(prev => ({ ...prev, selectedBusiness: business }))
    handleAddToDisco(business)
  }

  let requireLogin = false
  if (user.error && user.error.providedLoginDetails) {
    switch (user.error.code) {
      case 'ADD_TO_DISCO_ERROR_UNAUTHORIZED':
      case 'ADD_TO_DISCO_ERROR_NO_DISCOS':
        requireLogin = true
    }
  }
  // SUB-1158: an empty list only means "sign in first" while the save is still
  // pending. On the success path there is nothing left to authenticate, and the
  // list is legitimately empty when the chooser was skipped for a single
  // business.
  if (!success && businesses.length === 0) requireLogin = true

  const open = showModal && !(success && hideSuccessModal)

  const renderContent = () => {
    if (playlistPasswordPrompt) {
      return (
        <PlaylistPasswordForm
          invalidPassword={playlistPasswordPrompt.invalidPassword}
          onSubmit={handlePlaylistPasswordSubmit}
        />
      )
    }

    if (success && !hideSuccessModal) {
      return (
        <AddToDiscoSuccessModal onClose={() => dispatch(addToDiscoCancel())} />
      )
    }

    if (requireLogin || (!user.loggedIn && user.mfaCodeRequired)) {
      return (
        <Flex minHeight="initial">
          <AddToDiscoLoginForm
            user={user}
            showSignIn={state.showSignIn}
            onShowSignIn={() =>
              setState(prev => ({ ...prev, showSignIn: true }))
            }
            onLoginUser={handleLogin}
            onForgotPassword={handleForgotPassword}
            onGetSignUpUrl={signupUrlAsync}
          />
        </Flex>
      )
    }

    return (
      <AddToDiscoBusinessList
        onClose={onRequestClose}
        onSave={handleSaveBusiness}
        state={state}
        businesses={businesses}
        currentBusinessId={currentBusinessId}
      />
    )
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => !isOpen && onRequestClose()}
    >
      <Dialog.Content className={styles.AddToDiscoModal_DialogContent}>
        <VisuallyHidden>
          <Dialog.Title>
            {t('shares:saveToDisco', 'Save to DISCO')}
          </Dialog.Title>
        </VisuallyHidden>
        {renderContent()}
      </Dialog.Content>
    </Dialog.Root>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default AddToDiscoModal as any
